import { keyArray } from '@solid-primitives/keyed';
import { useSystemAssets } from '@bz/systemAssets';
import Decimal from 'decimal.js-light';

import { BetSlipType, SimpleOdds, SingleSubType } from '../../types';
import { BetItem } from './BetItem';
import { BetInput } from './betItemCom/BetInput';
import { BetStakeInputTip } from './betItemCom/BetStakeInputTip';
import { useBonusConfig } from '../shared/hooks';
import { useBetSlipStore, getComboOrSystemBets } from '../../useBetSlip';
import { useMatchDetail } from '../match.context';

import { MarketStatus, OutcomeStatus } from '~/constants';
import { createLatestOdds } from '~/primitives/createOddsWs';
import '../css.css';

/**
 * 投注项列表组件
 * 显示所有已选择的投注项，按比赛分组显示
 */
export const BetItems = () => {
  const [store, dispatch] = useBetSlipStore();
  const [assets] = useSystemAssets();
  const { minBetOdds } = useBonusConfig();

  // 检查投注单是否为空
  const isEmpty = createMemo(() => store.odds.length === 0);

  const IsCombo = createMemo(() => store.betType === BetSlipType.Combo);

  const latestOdds = createMemo(
    keyArray(
      () => store.odds,
      (odd) => (odd.uniqueKey ? odd.uniqueKey : odd.code),
      (odd) => createLatestOdds(odd)
    )
  );

  const comboBets = createMemo(() =>
    latestOdds().filter((o) => o.type !== SingleSubType.CustomBet && o.type !== SingleSubType.Outright)
  );

  const totalOdds = createMemo(() => {
    const isAllActive = comboBets().every((item) => item.active === OutcomeStatus.Active);

    if (!isAllActive) return new Decimal(0);

    return comboBets().reduce((acc, odd) => {
      return acc.mul(new Decimal(odd.odds));
    }, new Decimal(1));
  });

  // 区分single combo system下的数据显示
  const displayOdds = createMemo(() => {
    const isSingle = store.betType === BetSlipType.Single;
    if (isSingle) return latestOdds();
    return getComboOrSystemBets(latestOdds());
  });

  const filterItem = (latestOdd: any) => {
    const filterMarketStatus =
      [MarketStatus.Active].includes(latestOdd.marketStatus) && latestOdd.active === OutcomeStatus.Active;
    return filterMarketStatus;
  };

  const isShowOddsLimit = createMemo(() => {
    const isSingle = store.betType === BetSlipType.Single;
    let finalOdds = latestOdds().filter((o) => filterItem(o));
    if (!isSingle) {
      finalOdds = latestOdds().filter(
        (odd) => odd.type !== SingleSubType.CustomBet && odd.type !== SingleSubType.Outright && filterItem(odd)
      );
    }

    return finalOdds.some((o) => o.odds < minBetOdds());
  });

  const renderOdds = keyArray(
    () => {
      if (store.odds.length === 0) return [];

      // 按比赛ID分组投注项
      const groupedByMatch: SimpleOdds[][] = [[]];
      let lastMatchId = store.odds[0].matchId;
      let currentGroup = groupedByMatch[0];

      for (const odd of displayOdds()) {
        if (odd.matchId !== lastMatchId) {
          lastMatchId = odd.matchId;
          currentGroup = [];
          groupedByMatch.push(currentGroup);
        }
        currentGroup.push(odd as any);
      }
      // 按照每场比赛，进行分组，并为每个分组渲染投注项
      return groupedByMatch;
    },
    (odds) => odds[0]?.matchId ?? 'invalid',
    (odds, index) => {
      const isNotCombinable = () => odds().length > 1 && store.betType !== BetSlipType.Single;

      const oddsRendered = keyArray(
        odds,
        (odd, index) => `${odd.code}__${odd.type}__${index}__${odd.matchId}`,
        (odd, ind) => {
          // 获取对应比赛的详情
          const match = useMatchDetail(() => odd().matchId);

          // 替换占位符：在 BetItems 层面统一处理
          const processedOdd = createMemo(() => {
            const m = match();
            const o = odd();

            if (!m || !o) return o;

            const home = m.homeName || 'Home';
            const away = m.awayName || 'Away';

            const replaceTeams = (text: string | undefined) => {
              if (!text) return text;
              return text.replace(/\{#competitor1\}/g, home).replace(/\{#competitor2\}/g, away);
            };

            // 处理 CustomBet 类型
            if (o.type === SingleSubType.CustomBet && o.selections) {
              return {
                ...o,
                selections: o.selections.map((s) => ({
                  ...s,
                  outcomeName: replaceTeams(s.outcomeName),
                  colName: replaceTeams(s.colName),
                  marketName: replaceTeams(s.marketName),
                })),
              };
            }

            // 普通 Odds 类型
            return {
              ...o,
              outcomeName: replaceTeams(o.outcomeName),
              colName: replaceTeams(o.colName),
              marketName: replaceTeams(o.marketName),
            };
          });

          // 说明渲染的是单独一个投注项，而非一组投注项
          const isLast = () => ind() === odds().length - 1;
          const itemClass = () => {
            if (isNotCombinable() && isLast()) return 'mb-0';
            return IsCombo() ? 'mb-[.06rem]' : 'mb-2';
          };

          return (
            <div class={itemClass()}>
              <BetItem
                index={index()}
                subIndex={ind()}
                odd={processedOdd()}
                onRemove={() => {
                  dispatch({
                    type: 'REMOVE_ODD',
                    uniqueKey: odd().uniqueKey,
                  });
                }}
              />
            </div>
          );
        }
      );

      const customRule = createMemo(() => {
        if (store.betType === BetSlipType.Single)
          return odds()
            .filter((o) => o.type === SingleSubType.CustomBet)
            .some((s) => s.selections.length < 2);
      });

      const isNotCombinableClass = () => {
        if (isNotCombinable() && IsCombo()) {
          return `border-[1px] border-orange ${index() === 0 && 'rounded-t-lg'}`;
        } else if (isNotCombinable() && store.betType == BetSlipType.System) {
          return 'border-[1px] border-orange rounded-lg';
        }
      };

      return (
        <div>
          {/* 投注项组容器 */}
          <div class={cx('flex flex-col', isNotCombinableClass())}>{oddsRendered()}</div>

          {/* 不可组合提示 */}
          <Show when={isNotCombinable()}>
            <div class="my-2 h-7 flex gap-1 items-center bg-alpha-function-orange-a10 text-function-alert text-xs font-semibold px-2 py-1 rounded-[100rem]">
              <IconCheckWarning class="size-4" />
              {t('Selections cannot be combined')}
            </div>
          </Show>

          <Show when={customRule()}>
            <div class="mb-2 h-7 flex gap-1 items-center bg-alpha-function-orange-a10 text-function-alert text-xs font-semibold px-2 py-1 rounded-[100rem]">
              <IconCheckWarning class="size-4" />
              {t('Custom bet Selections must more than 2')}
            </div>
          </Show>
        </div>
      );
    }
  );

  const ComboTotal = () => {
    const getOdds = createMemo(() => totalOdds().toDecimalPlaces(2, Decimal.ROUND_DOWN).toFixed(2));

    return (
      <div class="bg-layer5-card p-2 mb-2 rounded-br-lg rounded-bl-lg">
        <span class="flex justify-between font-semibold mb-2">
          <span class="text-neutral-main2">{t('Total Odds')}</span>
          <span class="text-neutral-main1">
            <Show when={Number(getOdds()) > 0} fallback={<span>{t('-')}</span>}>
              <OddsFormat value={Number(getOdds())} />
            </Show>
          </span>
        </span>
        <BetInput amount={store.comboAmount} betType={BetSlipType.Combo} />
        <Show when={store.comboAmount !== 0}>
          <BetStakeInputTip amount={store.comboAmount} isShowOddsLimit={isShowOddsLimit} />
        </Show>
      </div>
    );
  };

  return (
    <div class={cx('flex flex-col px-3 pt-2 bg-betslip-body-bg')}>
      <Show
        when={!isEmpty()}
        fallback={
          // 空状态显示
          <div class="flex flex-col items-center py-6 px-8">
            <img src={assets.emptyGeneral} class="size-24" alt="Empty State" />
            <div class="text-sm font-semibold mb-1 text-neutral-main1">{t('Betslip is Empty')}</div>
            <div class="text-neutral-main2 text-sm font-semibold">{t('Place Your Bet')}</div>
          </div>
        }
      >
        {renderOdds()}
        <Show when={store.betType === BetSlipType.Combo}>
          <ComboTotal />
        </Show>
        <div class="mb-1" />
      </Show>
    </div>
  );
};
